import React, { useState, useEffect, useRef } from "react";
import "./RecWiseCode.css";
import { useNavigate } from 'react-router-dom';
import CTA from './CTA';

const ComplianceManagementModule = () => {
  const [activeSection, setActiveSection] = useState("regulatory-tracking");
  const [visibleSections, setVisibleSections] = useState(new Set());
  const sectionsRef = useRef({});
  const navigate = useNavigate();
  const handleCompliance = () => navigate('/access-recruit');

  const sectionContent = {
    "regulatory-tracking": {
      title: "Regulatory Change Tracking",
      description: "Stay ahead of regulatory changes with automated tracking of labor laws, employment regulations, and compliance requirements across multiple jurisdictions. Monitor updates from government agencies, legislative bodies, and regulatory authorities. Receive alerts for new regulations, deadline changes, and compliance requirements. Maintain a centralized knowledge base of regulatory requirements with version control and historical tracking. Ensure your organization remains compliant with evolving employment laws and regulations."
    },
    "policy-management": {
      title: "Policy Management",
      description: "Create, manage, and distribute organizational policies with automated workflows and compliance tracking. Develop policy documents with version control, approval workflows, and effective date management. Distribute policies to relevant employees with automated acknowledgment tracking and signature requirements. Maintain policy libraries with search capabilities, categorization, and access controls. Track policy readership, acknowledgments, and compliance rates across the organization for comprehensive policy governance."
    },
    "audit-management": {
      title: "Audit Preparation",
      description: "Prepare for internal and external audits with systematic documentation, evidence collection, and compliance verification. Create audit checklists, schedule audit activities, and assign responsibilities. Collect and organize evidence for compliance verification, including documents, reports, and process records. Track audit findings, remediation actions, and follow-up requirements. Generate audit reports, compliance certificates, and improvement plans. Ensure audit readiness at all times with continuous compliance monitoring and documentation."
    },
    "risk-assessment": {
      title: "Compliance Risk Assessment",
      description: "Identify, assess, and mitigate compliance risks through systematic risk management processes. Conduct regular compliance risk assessments across different departments, locations, and processes. Evaluate risk likelihood, impact, and mitigation strategies. Track risk mitigation actions, responsibilities, and deadlines. Generate risk heat maps, compliance dashboards, and risk reports for management review. Implement proactive risk management to prevent compliance violations and regulatory penalties."
    },
    "compliance-reporting": {
      title: "Automated Compliance",
      description: "Generate regulatory reports automatically with built-in validation, accuracy checks, and submission tracking. Create EEO-1 reports, OSHA logs, pay equity analyses, and other statutory compliance documents. Schedule automated report generation and submission for recurring compliance requirements. Maintain audit trails for all compliance reporting activities. Track submission status, confirmation receipts, and compliance certification. Ensure timely and accurate reporting across all regulatory requirements."
    }
  };

  const sections = [
    { id: "regulatory-tracking", label: "REGULATORY" },
    { id: "policy-management", label: "POLICIES" },
    { id: "audit-management", label: "AUDITS" },
    { id: "risk-assessment", label: "RISK" },
    { id: "compliance-reporting", label: "REPORTING" }
  ];

  useEffect(() => {
    const onScroll = () => {
      const pos = window.scrollY + 150;
      for (const sec of sections) {
        const el = sectionsRef.current[sec.id];
        if (!el) continue;
        if (pos >= el.offsetTop && pos < el.offsetTop + el.offsetHeight) {
          setActiveSection(sec.id);
          break;
        }
      }
    };
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((en) => {
          if (en.isIntersecting) {
            setVisibleSections((prev) => new Set([...prev, en.target.dataset.section]));
          }
        });
      },
      { threshold: 0.15 }
    );
    Object.values(sectionsRef.current).forEach((el) => el && obs.observe(el));
    return () => obs.disconnect();
  }, []);

  const scrollToSection = (id) => {
    const el = sectionsRef.current[id];
    if (el) window.scrollTo({ top: el.offsetTop - 100, behavior: "smooth" });
  };

  return (
    <div className="resto-main-wrapper">
      {/* <nav className="resto-sticky-navbar">
        <div className="resto-nav-container">
          {sections.map((s) => (
            <button
              key={s.id}
              className={`resto-nav-button ${activeSection === s.id ? "resto-active" : ""}`}
              onClick={() => scrollToSection(s.id)}
            >
              {s.label}
            </button>
          ))}
        </div>
      </nav> */}

      <div className="resto-content-wrapper">

        {/* 1. Regulatory Tracking */}
        <section
          ref={(el) => (sectionsRef.current["regulatory-tracking"] = el)}
          data-section="regulatory-tracking"
          className={`resto-section ${visibleSections.has("regulatory-tracking") ? "resto-visible" : ""}`}
        >
          <div className="resto-section-content resto-fade-in-left">
            <div className="resto-section-label">Regulatory</div>
            <h2 className="resto-section-title">
              {sectionContent["regulatory-tracking"].title}
            </h2>
            <p className="resto-section-description">
              {sectionContent["regulatory-tracking"].description}
            </p>

            <div className="resto-buttons-group">
              <button className="resto-primary-btn" onClick={handleCompliance}>Track Regulations</button>
              <button className="resto-secondary-btn" onClick={handleCompliance}>View Updates</button>
            </div>
          </div>

          <div className="resto-section-image resto-fade-in-right">
            <img src="/images/comi1.png" className="resto-real-image" alt="Regulatory Tracking" />
          </div>
        </section>

        {/* 2. Policy Management */}
        <section
          ref={(el) => (sectionsRef.current["policy-management"] = el)}
          data-section="policy-management"
          className={`resto-section resto-reverse ${visibleSections.has("policy-management") ? "resto-visible" : ""}`}
        >
          <div className="resto-section-content resto-fade-in-right">
            <div className="resto-section-label">Policies</div>
            <h2 className="resto-section-title">
              {sectionContent["policy-management"].title}
            </h2>
            <p className="resto-section-description">
              {sectionContent["policy-management"].description}
            </p>

            <div className="resto-buttons-group">
              <button className="resto-primary-btn" onClick={handleCompliance}>Manage Policies</button>
              <button className="resto-secondary-btn" onClick={handleCompliance}>Track Acknowledgments</button>
            </div>
          </div>

          <div className="resto-section-image resto-fade-in-left">
            <img src="/images/comi2.png" className="resto-real-image" alt="Regulatory Tracking" />
          </div>
        </section>

        <CTA/>

        {/* 3. Audit Management */}
        <section
          ref={(el) => (sectionsRef.current["audit-management"] = el)}
          data-section="audit-management"
          className={`resto-section ${visibleSections.has("audit-management") ? "resto-visible" : ""}`}
        >
          <div className="resto-section-content resto-fade-in-left">
            <div className="resto-section-label">Audits</div>
            <h2 className="resto-section-title">
              {sectionContent["audit-management"].title}
            </h2>
            <p className="resto-section-description">
              {sectionContent["audit-management"].description}
            </p>

            <div className="resto-buttons-group">
              <button className="resto-primary-btn" onClick={handleCompliance}>Prepare Audit</button>
              <button className="resto-secondary-btn" onClick={handleCompliance}>Track Findings</button>
            </div>
          </div>

          <div className="resto-section-image resto-fade-in-right">
            <img src="/images/comi3.png" className="resto-real-image" alt="Regulatory Tracking" />
          </div>
        </section>

        {/* 4. Risk Assessment */}
        <section
          ref={(el) => (sectionsRef.current["risk-assessment"] = el)}
          data-section="risk-assessment"
          className={`resto-section resto-reverse ${visibleSections.has("risk-assessment") ? "resto-visible" : ""}`}
        >
          <div className="resto-section-content resto-fade-in-right">
            <div className="resto-section-label">Risk</div>
            <h2 className="resto-section-title">
              {sectionContent["risk-assessment"].title}
            </h2>
            <p className="resto-section-description">
              {sectionContent["risk-assessment"].description}
            </p>

            <div className="resto-buttons-group">
              <button className="resto-primary-btn" onClick={handleCompliance}>Assess Risks</button>
              <button className="resto-secondary-btn" onClick={handleCompliance}>View Risk Matrix</button>
            </div>
          </div>

          <div className="resto-section-image resto-fade-in-left">
            <img src="/images/comi4.png" className="resto-real-image" alt="Regulatory Tracking" />
          </div>
        </section>

        {/* 5. Compliance Reporting */}
        <section
          ref={(el) => (sectionsRef.current["compliance-reporting"] = el)}
          data-section="compliance-reporting"
          className={`resto-section ${visibleSections.has("compliance-reporting") ? "resto-visible" : ""}`}
        >
          <div className="resto-section-content resto-fade-in-left">
            <div className="resto-section-label">Reporting</div>
            <h2 className="resto-section-title">
              {sectionContent["compliance-reporting"].title}
            </h2>
            <p className="resto-section-description">
              {sectionContent["compliance-reporting"].description}
            </p>

            <div className="resto-buttons-group">
              <button className="resto-primary-btn" onClick={handleCompliance}>Generate Reports</button>
              <button className="resto-secondary-btn" onClick={handleCompliance}>Track Submissions</button>
            </div>
          </div>

          <div className="resto-section-image resto-fade-in-right">
            <img src="/images/comi5.png" className="resto-real-image" alt="Regulatory Tracking" />
          </div>
        </section>

      </div>
    </div>
  );
};

export default ComplianceManagementModule;